import api from './api';
import { encryptPassword, generateNonce, generateTimestamp } from '../utils/encryption';

export const login = async (username, password) => {
  try {
    const response = await api.post('/admin/login/', {
      username,
      password,
    });
    const { access, refresh } = response.data;
    localStorage.setItem('token', access);
    localStorage.setItem('refresh_token', refresh);
    const userProfile = await getCurrentUser();
    return { token: access, refresh_token: refresh, user: userProfile };
  } catch (error) {
    let userMessage = 'Login failed. Please check your username and password or contact support.';
    if (error.response?.status === 404) {
      userMessage = 'Login failed. Please check your username and password or contact support.';
    } else if (error.response?.data?.detail) {
      userMessage = error.response.data.detail;
    } else if (error.response?.data?.message) {
      userMessage = error.response.data.message;
    }
    throw new Error(userMessage);
  }
};

export const secureLogin = async (username, password) => {
  try {
    const clientNonce = generateNonce();
    const timestamp = generateTimestamp();
    const encryptedPassword = encryptPassword(password, clientNonce, clientNonce);
    try {
      const response = await api.post('/admin/secure-login/', {
        username,
        password: encryptedPassword,
        client_nonce: clientNonce,
        timestamp,
      });
      const { access, refresh, server_nonce } = response.data;
      localStorage.setItem('token', access);
      localStorage.setItem('refresh_token', refresh);
      const userProfile = await getCurrentUser();
      return { token: access, refresh_token: refresh, user: userProfile, server_nonce };
    } catch (error) {
      let userMessage = 'Login failed. Please check your username and password or contact support.';
      if (error.response?.status === 404) {
        userMessage = 'Login failed. Please check your username and password or contact support.';
      } else if (error.response?.data?.non_field_errors) {
        userMessage = error.response.data.non_field_errors[0];
      } else if (error.response?.data?.detail) {
        userMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        userMessage = error.response.data.message;
      }
      throw new Error(userMessage);
    }
  } catch (error) {
    console.error('Secure login error:', error);
    throw error;
  }
};

export const logout = async () => {
  try {
    const refresh_token = localStorage.getItem('refresh_token');
    if (refresh_token) {
      await api.post('/admin/invalidate-token/', {
        refresh_token: refresh_token
      });
    }
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('loginLockout');
  }
};

export const refreshToken = async () => {
  try {
    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) {
      throw new Error('No refresh token available');
    }
    const response = await api.post('/admin/refresh/', {
      refresh: refresh_token
    });
    const { access } = response.data;
    localStorage.setItem('token', access);
    return access;
  } catch (error) {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    throw new Error('Token refresh failed');
  }
};

export const invalidateToken = async () => {
  try {
    const refresh_token = localStorage.getItem('refresh_token');
    if (refresh_token) {
      await api.post('/admin/invalidate-token/', {
        refresh_token: refresh_token
      });
    }
  } catch (error) {
    console.error('Token invalidation error:', error);
  } finally {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('loginLockout');
  }
};

export const getCurrentUser = async () => {
  try {
    const response = await api.get('/admin/user/');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to get user data');
  }
};

export const getToken = () => {
  return localStorage.getItem('token');
};

export const getRefreshToken = () => {
  return localStorage.getItem('refresh_token');
};

export const isAuthenticated = () => {
  return !!getToken();
};

export const sendVerificationCode = async (data) => {
  try {
    const response = await api.post('/admin/send-verification-code/', data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to send verification code');
  }
};

export const verifyCode = async (data) => {
  try {
    const response = await api.post('/admin/verify-code/', data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to verify code');
  }
};

export const resetPassword = async (identifier, code, newPassword, useEmail = false) => {
  try {
    const clientNonce = generateNonce();
    const timestamp = generateTimestamp();
    const encryptedPassword = encryptPassword(newPassword, clientNonce, clientNonce);
    
    const payload = {
      code,
      new_password: encryptedPassword,
      confirm_password: encryptedPassword,
      client_nonce: clientNonce,
      timestamp: timestamp,
    };

    // Add either phone or email based on the method
    if (useEmail) {
      payload.email = identifier;
    } else {
      payload.phone = identifier;
    }

    const response = await api.post('/admin/reset-password/', payload);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to reset password');
  }
};

export const updateProfile = async (profileData) => {
  try {
    const response = await api.post('/admin/update-profile/', profileData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to update profile');
  }
};

export const updateApprovalPin = async (approvalPin, otp) => {
  try {
    const response = await api.post('/admin/users/update-approval-pin/', {
      approval_pin: approvalPin,
      otp: otp
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to update approval PIN');
  }
}; 
